import { apiClient } from "./client";
import type {
  SimulationAttachment,
  SimulationAttachmentUpdate,
  SimulationAttachmentLinkRequest,
  AttachmentUploadResponse,
} from "@/types";

const API_PREFIX = "/simulators";

/**
 * Upload a file attachment
 */
export async function uploadAttachment(
  versionId: number,
  file: File,
  options?: {
    title?: string;
    comment?: string;
    simulationId?: number;
  }
): Promise<AttachmentUploadResponse> {
  const formData = new FormData();
  formData.append("file", file);
  
  if (options?.title) {
    formData.append("title", options.title);
  }
  if (options?.comment) {
    formData.append("comment", options.comment);
  }
  if (options?.simulationId) {
    formData.append("simulation_id", options.simulationId.toString());
  }
  
  const response = await apiClient.post<AttachmentUploadResponse>(
    `${API_PREFIX}/versions/${versionId}/attachments/upload`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  
  return response.data;
}

/**
 * List attachments for a simulation
 */
export async function listSimulationAttachments(
  simulationId: number
): Promise<SimulationAttachment[]> {
  const response = await apiClient.get<SimulationAttachment[]>(
    `${API_PREFIX}/simulations/${simulationId}/attachments`
  );
  return response.data;
}

/**
 * List temporary attachments by their tokens
 */
export async function listTempAttachments(
  versionId: number,
  tempTokens: string[]
): Promise<SimulationAttachment[]> {
  if (tempTokens.length === 0) {
    return [];
  }
  
  const response = await apiClient.get<SimulationAttachment[]>(
    `${API_PREFIX}/versions/${versionId}/attachments/temp`,
    {
      params: {
        temp_tokens: tempTokens.join(","),
      },
    }
  );
  return response.data;
}

/**
 * Link temporary attachments to a simulation
 */
export async function linkAttachments(
  simulationId: number,
  tempTokens: string[]
): Promise<SimulationAttachment[]> {
  const response = await apiClient.post<SimulationAttachment[]>(
    `${API_PREFIX}/simulations/${simulationId}/attachments/link`,
    { temp_tokens: tempTokens } as SimulationAttachmentLinkRequest
  );
  return response.data;
}

/**
 * Update attachment metadata
 */
export async function updateAttachment(
  attachmentId: number,
  data: SimulationAttachmentUpdate
): Promise<SimulationAttachment> {
  const response = await apiClient.patch<SimulationAttachment>(
    `${API_PREFIX}/attachments/${attachmentId}`,
    data
  );
  return response.data;
}

/**
 * Delete an attachment
 */
export async function deleteAttachment(
  attachmentId: number
): Promise<void> {
  await apiClient.delete(`${API_PREFIX}/attachments/${attachmentId}`);
}

/**
 * Get a single attachment
 */
export async function getAttachment(
  attachmentId: number
): Promise<SimulationAttachment> {
  const response = await apiClient.get<SimulationAttachment>(
    `${API_PREFIX}/attachments/${attachmentId}`
  );
  return response.data;
}

/**
 * Helper to format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  
  const k = 1024;
  const sizes = ["B", "Ko", "Mo", "Go"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return (bytes / Math.pow(k, i)).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 1 }) + " " + sizes[i];
}

/**
 * Helper to get icon name based on content type
 */
export function getFileIconType(contentType: string): "pdf" | "word" | "excel" | "image" | "archive" | "file" {
  if (contentType === "application/pdf") return "pdf";
  if (contentType.includes("word") || contentType.includes("document")) return "word";
  if (contentType.includes("excel") || contentType.includes("spreadsheet") || contentType === "text/csv") return "excel";
  if (contentType.startsWith("image/")) return "image";
  if (contentType.includes("zip") || contentType.includes("rar") || contentType.includes("7z") || contentType.includes("gzip")) return "archive";
  return "file";
}
